import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, ArrowUp, ArrowDown, Loader2, Check, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CATEGORY_COLORS, COLOR_LABELS, paletteFor } from "@/lib/categories";

export default function CategoriesManager() {
  const qc = useQueryClient();
  const { data: cats, isLoading } = useQuery({
    queryKey: ["admin-categories"],
    queryFn: () => api.get("/admin/categories").then((r) => r.data),
  });

  const [name, setName] = useState("");
  const [color, setColor] = useState("blue");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const list = cats || [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-categories"] });
    qc.invalidateQueries({ queryKey: ["categories"] });
  };

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.post("/admin/categories", { name: name.trim(), color });
      toast.success("Рубриката е добавена.");
      setName("");
      setColor("blue");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const update = async (id, payload) => {
    try {
      await api.put(`/admin/categories/${id}`, payload);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveName = async (id) => {
    if (!editName.trim()) {
      setEditId(null);
      return;
    }
    await update(id, { name: editName.trim() });
    toast.success("Рубриката е преименувана.");
    setEditId(null);
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[idx];
    const b = list[j];
    try {
      await Promise.all([
        api.put(`/admin/categories/${a.id}`, { order: b.order }),
        api.put(`/admin/categories/${b.id}`, { order: a.order }),
      ]);
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Изтриване на рубрика „${c.name}“?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Рубриката е изтрита.");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mt-6" data-testid="categories-manager">
      <h2 className="mb-4 font-display text-lg font-bold text-slate-900">Рубрики</h2>

      <form
        onSubmit={add}
        className="mb-6 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4"
      >
        <div className="flex-1">
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Име на нова рубрика
          </label>
          <input
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary"
            data-testid="new-category-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="напр. Спорт, Култура, Образование"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
            Цвят
          </label>
          <select
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary"
            data-testid="new-category-color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          >
            {CATEGORY_COLORS.map((c) => (
              <option key={c} value={c}>{COLOR_LABELS[c]}</option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={adding}
          data-testid="add-category-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-white hover:bg-[#0041A8] disabled:opacity-60"
        >
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Добави
        </button>
      </form>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-7 w-7 animate-spin text-primary" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="divide-y divide-slate-100">
            {list.map((c, idx) => {
              const meta = paletteFor(c.color);
              return (
                <div key={c.id} className="flex items-center gap-3 p-4" data-testid={`category-row-${c.slug}`}>
                  <span className={`h-6 w-6 shrink-0 rounded-full ${meta.swatch}`} />
                  <div className="min-w-0 flex-1">
                    {editId === c.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          className="rounded border border-slate-200 px-2 py-1 text-sm outline-none focus:border-primary"
                          data-testid={`edit-category-input-${c.slug}`}
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && saveName(c.id)}
                        />
                        <button onClick={() => saveName(c.id)} className="rounded p-1 text-green-600 hover:bg-green-50" data-testid={`save-category-${c.slug}`}>
                          <Check className="h-4 w-4" />
                        </button>
                        <button onClick={() => setEditId(null)} className="rounded p-1 text-slate-400 hover:bg-slate-100">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-display text-sm font-bold text-slate-900">{c.name}</span>
                        <span className="text-xs text-slate-400">/{c.slug}</span>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                          {c.count} {c.count === 1 ? "статия" : "статии"}
                        </span>
                      </div>
                    )}
                  </div>

                  <select
                    className="rounded-lg border border-slate-200 px-2 py-1 text-xs outline-none focus:border-primary"
                    data-testid={`category-color-${c.slug}`}
                    value={c.color}
                    onChange={(e) => update(c.id, { color: e.target.value })}
                  >
                    {CATEGORY_COLORS.map((col) => (
                      <option key={col} value={col}>{COLOR_LABELS[col]}</option>
                    ))}
                  </select>

                  <div className="flex items-center gap-0.5">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30" aria-label="Нагоре">
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 disabled:opacity-30" aria-label="Надолу">
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => { setEditId(c.id); setEditName(c.name); }}
                      data-testid={`edit-category-${c.slug}`}
                      className="rounded p-1.5 text-slate-500 hover:bg-blue-50 hover:text-primary"
                      aria-label="Преименувай"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => del(c)}
                      data-testid={`delete-category-${c.slug}`}
                      className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                      aria-label="Изтрий"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <p className="mt-3 text-xs text-slate-400">
        Рубрика с прикачени статии не може да бъде изтрита. Преместете или изтрийте статиите първо.
      </p>
    </div>
  );
}
